/**
 * Trackside advertising boards. Each board is a blocky hoarding with a canvas-drawn face.
 * Affiliate boards open the tagged Amazon link when clicked; "advertise here" boards open
 * the configured contact, or do nothing but show the text when none is configured.
 */
import * as THREE from 'three';
import { mulberry32 } from '@typing-racer/shared';
import { box } from './styles/blocky.js';
import { ROAD_WIDTH, type Track } from './track.js';
import type { AffiliatePayload } from '../screens/affiliate.js';

export interface BoardInfo {
  label: string;
  url: string | null;
  kind: 'affiliate' | 'advertise';
}

const W = 8;
const H = 3.6;

function face(label: string, kind: BoardInfo['kind']): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 460;
  const ctx = c.getContext('2d')!;
  const ad = kind === 'advertise';
  ctx.fillStyle = ad ? '#fdd835' : '#ffffff';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.strokeStyle = '#1b2430';
  ctx.lineWidth = 28;
  ctx.strokeRect(14, 14, c.width - 28, c.height - 28);
  ctx.fillStyle = '#1b2430';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const font = (px: number, weight = 700) => `${weight} ${px}px Fredoka, Nunito, "Segoe UI", system-ui, sans-serif`;
  // Fit the label on one or two lines.
  const words = label.split(' ');
  const lines: string[] = [];
  let cur = '';
  ctx.font = font(96);
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(t).width > c.width - 120 && cur) {
      lines.push(cur);
      cur = w;
    } else cur = t;
  }
  if (cur) lines.push(cur);
  const size = lines.length > 1 ? 86 : 104;
  ctx.font = font(size);
  const top = ad ? 150 : 175;
  lines.slice(0, 2).forEach((l, i) => ctx.fillText(l, c.width / 2, top + i * (size + 8) - (lines.length > 1 ? 30 : 0)));
  ctx.font = font(40, 600);
  ctx.fillStyle = ad ? '#1b2430' : '#5b6878';
  ctx.fillText(ad ? 'Your brand on this track. Get in touch.' : 'amazon.co.uk  ·  affiliate link', c.width / 2, c.height - 90);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/** Build all boards for the track. Returns a group plus the clickable panel meshes. */
export function buildBillboards(track: Track, ads: AffiliatePayload, count = 18): { group: THREE.Group; panels: THREE.Mesh[] } {
  const group = new THREE.Group();
  const panels: THREE.Mesh[] = [];
  const rng = mulberry32(777);
  const up = new THREE.Vector3(0, 1, 0);
  const items: BoardInfo[] = [];
  const links = ads.links;
  if (links.length === 0 && !ads.advertise.text) return { group, panels };
  for (let i = 0; i < count; i++) {
    // Every fifth board invites advertisers; the rest cycle through the affiliate products.
    const advertiseSlot = links.length === 0 || (i + 2) % 5 === 0;
    if (advertiseSlot) items.push({ label: ads.advertise.text, url: ads.advertise.url, kind: 'advertise' });
    else {
      const l = links[i % links.length]!;
      items.push({ label: l.label, url: l.url, kind: 'affiliate' });
    }
  }
  const textures = new Map<string, THREE.CanvasTexture>();
  items.forEach((item, i) => {
    // Spread boards around the lap, away from the start/finish straight, alternating sides.
    const u = 0.06 + (i / count) * 0.88 + (rng() - 0.5) * 0.02;
    const p = track.curve.getPointAt(u);
    const t = track.curve.getTangentAt(u);
    const n = new THREE.Vector3().crossVectors(up, t).normalize();
    const side = i % 2 === 0 ? 1 : -1;
    const pos = p.clone().addScaledVector(n, side * (ROAD_WIDTH / 2 + 3.5));
    const board = new THREE.Group();
    board.position.copy(pos);
    // Face mostly against the direction of travel (towards approaching cars) and partly
    // towards the road, so both the chase and side cameras can read it.
    const toRoad = n.clone().multiplyScalar(-side);
    const facing = t.clone().multiplyScalar(-Math.cos(0.55)).addScaledVector(toRoad, Math.sin(0.55)).normalize();
    board.rotation.y = Math.atan2(facing.x, facing.z);
    board.add(box(0.4, 3.2, 0.4, '#546e7a', -W / 2 + 0.6, 1.6, 0, 0.05));
    board.add(box(0.4, 3.2, 0.4, '#546e7a', W / 2 - 0.6, 1.6, 0, 0.05));
    board.add(box(W + 0.3, H + 0.3, 0.3, '#1b2430', 0, 3.2 + H / 2, -0.12, 0.05));
    const key = `${item.kind}:${item.label}`;
    let tex = textures.get(key);
    if (!tex) {
      tex = face(item.label, item.kind);
      textures.set(key, tex);
    }
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(W, H), new THREE.MeshBasicMaterial({ map: tex }));
    panel.position.set(0, 3.2 + H / 2, 0.06);
    panel.userData = { board: item } as { board: BoardInfo };
    board.add(panel);
    panels.push(panel);
    group.add(board);
  });
  return { group, panels };
}
